import React, { useEffect, useMemo, useRef, useState } from 'react';
import Select from 'react-select';
import './WorkflowSequence.css';

export const sequenceIds = sequence => String(sequence || '').split(',').map(id => id.trim()).filter(Boolean);
const processName = process => process.Name || 'Unnamed process';

export async function loadProcesses() {
    const response = await fetch('process_list', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8' },
        body: 'startIndex=-1'
    });
    if (!response.ok) throw new Error('Could not load processes.');
    const processes = await response.json();
    if (!Array.isArray(processes) || processes.some(process => !/^\d+$/.test(String(process.ProcessId)))) {
        throw new Error('Invalid process list.');
    }
    return processes;
}

export function SequenceSummary({ sequence, processes, loaded }) {
    const ids = sequenceIds(sequence);
    if (!ids.length) return <span className="workflow-sequence-empty">No steps</span>;
    const byId = new Map(processes.map(process => [String(process.ProcessId), process]));
    return <ol className="workflow-sequence-summary" aria-label="Process sequence">
        {ids.map((id, index) => {
            const process = byId.get(id);
            return <li key={index} className={!process && loaded ? 'workflow-step-missing' : ''}
                title={process ? [process.Description, process.Command].filter(Boolean).join(' — ') : ''}>
                <span className="workflow-step-number">{index + 1}</span>
                <span>{process ? processName(process) : loaded ? 'Unavailable process' : 'Process'}</span>
                <small>#{id}</small>
            </li>;
        })}
    </ol>;
}

export default function WorkflowSequence({ value, onChange, processes = [], loading, loaded, error, onRefresh, inputId }) {
    const [replacing, setReplacing] = useState(null);
    const [notice, setNotice] = useState('');
    const [menuOpen, setMenuOpen] = useState(false);
    const picker = useRef(null);
    useEffect(() => { if (replacing !== null && picker.current) picker.current.focus(); }, [replacing]);
    const ids = sequenceIds(value);
    const byId = new Map(processes.map(process => [String(process.ProcessId), process]));
    const options = useMemo(() => processes.map(process => ({
        value: String(process.ProcessId), label: processName(process), process,
        search: [process.Name, process.Description, process.Command, process.ProcessId].filter(Boolean).join(' ').toLowerCase()
    })), [processes]);
    const change = (next, message) => {
        onChange(next.join(','));
        setNotice(message);
        setReplacing(null);
    };
    const move = (index, direction) => {
        const next = [...ids];
        [next[index], next[index + direction]] = [next[index + direction], next[index]];
        change(next, `Step moved to position ${index + direction + 1}.`);
    };
    return <section className="workflow-sequence-editor" aria-label="Workflow sequence">
        <div className="workflow-sequence-heading"><h2>Sequence</h2><span>{ids.length} {ids.length === 1 ? 'step' : 'steps'}</span></div>
        <p className="workflow-sequence-help">Processes run from top to bottom. Search to add a step, or select a step to replace it.</p>
        {ids.length ? <ol className="workflow-sequence-steps">
            {ids.map((id, index) => {
                const process = byId.get(id);
                const name = process ? processName(process) : loaded ? 'Unavailable process' : 'Process';
                return <li key={index} className={`${replacing === index ? 'workflow-step-active ' : ''}${!process && loaded ? 'workflow-step-missing' : ''}`}>
                    <span className="workflow-step-number">{index + 1}</span>
                    <button type="button" className="workflow-step-details" aria-label={`Replace step ${index + 1}: ${name}`} aria-pressed={replacing === index}
                        onClick={() => { setReplacing(index); setMenuOpen(true); onRefresh(); }}>
                        <span><strong>{name}</strong> <small>#{id}</small></span>
                        {process && process.Description && <span className="workflow-step-description">{process.Description}</span>}
                        {process && process.Command && <code>{process.Command}</code>}
                        {!process && loaded && <span className="workflow-step-description">This process is no longer in the catalogue. Replace or remove this step.</span>}
                    </button>
                    <div className="workflow-step-actions">
                        <button type="button" disabled={index === 0} aria-label={`Move step ${index + 1} up`} title="Move up" onClick={() => move(index, -1)}>↑</button>
                        <button type="button" disabled={index === ids.length - 1} aria-label={`Move step ${index + 1} down`} title="Move down" onClick={() => move(index, 1)}>↓</button>
                        <button type="button" aria-label={`Remove step ${index + 1}: ${name}`} title="Remove step"
                            onClick={() => change(ids.filter((_, i) => i !== index), `Step ${index + 1} removed.`)}>×</button>
                    </div>
                </li>;
            })}
        </ol> : <div className="workflow-sequence-placeholder">No steps yet. Choose the first process below.</div>}
        <div className="workflow-picker-heading">
            <label htmlFor={inputId}>{replacing === null ? 'Add a process' : `Replace step ${replacing + 1}`}</label>
            {replacing !== null && <button type="button" className="workflow-text-button" onClick={() => setReplacing(null)}>Cancel replacement</button>}
            <button type="button" className="workflow-text-button" disabled={loading} onClick={onRefresh}>Refresh processes</button>
        </div>
        <Select ref={picker} inputId={inputId} instanceId={inputId} options={options} value={null} isLoading={loading}
            classNamePrefix="workflow-process-select" placeholder="Search by name, description or command…"
            menuIsOpen={menuOpen} onMenuOpen={() => { setMenuOpen(true); onRefresh(); }} onMenuClose={() => setMenuOpen(false)}
            filterOption={(option, query) => query.toLowerCase().trim().split(/\s+/).every(word => option.data.search.includes(word))}
            formatOptionLabel={option => <div className="workflow-process-option">
                <strong>{option.label}</strong> <small>#{option.value}</small>
                {option.process.Description && <span>{option.process.Description}</span>}
                {option.process.Command && <code>{option.process.Command}</code>}
            </div>}
            noOptionsMessage={() => loading ? 'Loading processes…' : error ? 'Processes could not be loaded. Try Refresh processes.' : 'No matching processes'}
            onChange={option => {
                if (!option) return;
                const next = [...ids];
                if (replacing === null) next.push(option.value);
                else next[replacing] = option.value;
                change(next, `${option.label} ${replacing === null ? 'added' : 'selected'}.`);
            }} />
        {error && <p role="alert" className="workflow-catalog-error">{error} {loaded ? 'Showing the last loaded processes.' : 'Refresh to try again.'}</p>}
        <span role="status" className="sr-only">{notice}</span>
    </section>;
}
